// Rediseño mayor 2026-08-31 (Módulo F) — pura, testable sin MockClient:
// decide qué "grupos" de especialidad (Tramo B) muestra el sidebar de la
// ficha del paciente. Dos filtros en serie:
//  1) el TECHO tenant-wide (especialidadesHabilitadas de Organization, ya
//     existente) — un grupo sin ningún código habilitado ahí no se muestra
//     nunca, sea cual sea la especialidad del practicante.
//  2) la especialidad del PRACTICANTE logueado (PractitionerRole) — el
//     filtro real de qué puede CREAR. `codigosPracticante === null` es el
//     caso de una cuenta legada sin PractitionerRole todavía (de antes de
//     este módulo): no restringe nada, mismo comportamiento que existía
//     antes de este cambio, para no sacarle a nadie una pestaña que ya tenía.
export type EntradaTramoB = { readonly codigo: string; readonly grupo: string };

/**
 * Grupos que se muestran a CUALQUIER especialidad, no solo a la que los
 * "posee" en el catálogo — a pedido explícito del usuario 2026-09-01:
 * Seguridad del Paciente aplica transversalmente a cualquier practicante, no
 * es exclusiva de ninguna especialidad puntual (de hecho ninguna especialidad
 * del Excel la pide explícitamente — ver catalogo-especialidades.test.ts).
 * UCI SÍ había pasado por acá brevemente el mismo día, pero el usuario pidió
 * revertirlo: queda exclusiva de Medicina Crítica / Terapia Intensiva (su
 * paquete `uciAdulto` en catalogo-especialidades.ts), no universal. Sigue
 * atado al techo tenant-wide (punto 1 de abajo) como cualquier otro grupo —
 * esto solo bypassea el filtro de especialidad (punto 2).
 */
const GRUPOS_UNIVERSALES = new Set(['seguridad-paciente']);

export function calcularGruposHabilitados(
  gruposDisponibles: readonly string[],
  tramoB: readonly EntradaTramoB[],
  codigosTecho: readonly string[],
  codigosPracticante: readonly string[] | null,
): string[] {
  return gruposDisponibles.filter((grupo) => {
    const codigosDelGrupo = tramoB.filter((e) => e.grupo === grupo).map((e) => e.codigo);
    const dentroDelTecho = codigosDelGrupo.some((c) => codigosTecho.includes(c));
    if (!dentroDelTecho) return false;
    if (codigosPracticante === null || GRUPOS_UNIVERSALES.has(grupo)) return true;
    return codigosDelGrupo.some((c) => codigosPracticante.includes(c));
  });
}

/**
 * "Extras sueltos" — códigos que el practicante tiene habilitados
 * (`codigosConExtras`, de `codigosFormularioDeEspecialidad`) pero que
 * pertenecen a un grupo Tramo B que NO le corresponde por paquete
 * (`codigosDePaquetes`, de `codigosDePaquetesDeEspecialidad` — sin
 * extras). Ver el comentario largo de `codigosDePaquetesDeEspecialidad`
 * (catalogoEspecialidades.ts) para el caso real que motivó esto: Medicina
 * General con `esc-phq9` (grupo "psicologia") sin el resto del paquete de
 * Psicología Clínica. Estos códigos se muestran junto a Tramo A (siempre
 * visibles, arriba de todo) en vez de vivir en una pestaña de grupo ajena
 * — pedido explícito del usuario 2026-09-02. `codigosPracticante === null`
 * (cuenta legada sin especialidad) no tiene extras sueltos: no hay
 * restricción de la que "sobresalgan".
 */
export function extrasSueltos(tramoB: readonly EntradaTramoB[], codigosDePaquetes: readonly string[] | null, codigosConExtras: readonly string[] | null): string[] {
  if (codigosDePaquetes === null || codigosConExtras === null) return [];
  const gruposCubiertos = new Set(tramoB.filter((e) => codigosDePaquetes.includes(e.codigo)).map((e) => e.grupo));
  return codigosConExtras.filter((c) => {
    if (codigosDePaquetes.includes(c)) return false;
    const entrada = tramoB.find((e) => e.codigo === c);
    return entrada !== undefined && !gruposCubiertos.has(entrada.grupo);
  });
}
